import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { featureFlagsApi } from '@/lib/api';
import { useAuthStore } from '@/hooks/useAuthStore';

type FlagCategory = 'general' | 'admin' | 'user' | 'experimental';

interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  category: FlagCategory;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
}

interface FlagStats {
  total: number;
  enabled: number;
  disabled: number;
  byCategory: Record<string, { total: number; enabled: number }>;
}

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  admin: 'Admin',
  user: 'Usuario',
  experimental: 'Experimental',
};

const CATEGORY_COLORS: Record<string, string> = {
  general: 'bg-gray-100 text-gray-800',
  admin: 'bg-blue-100 text-blue-800',
  user: 'bg-green-100 text-green-800',
  experimental: 'bg-purple-100 text-purple-800',
};

export default function FeatureFlags() {
  const { staff } = useAuthStore();
  const isSuperAdmin = staff?.role === 'SUPER_ADMIN';
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFlag, setNewFlag] = useState<{
    key: string;
    name: string;
    description: string;
    enabled: boolean;
    category: FlagCategory;
  }>({
    key: '',
    name: '',
    description: '',
    enabled: false,
    category: 'general',
  });

  // Fetch flags
  const { data: flags, isLoading } = useQuery({
    queryKey: ['feature-flags', selectedCategory],
    queryFn: async () => {
      const category = selectedCategory === 'all' ? undefined : selectedCategory;
      const response = await featureFlagsApi.getAll(category);
      return response.data as FeatureFlag[];
    },
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['feature-flags-stats'],
    queryFn: async () => {
      const response = await featureFlagsApi.getStats();
      return response.data as FlagStats;
    },
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      featureFlagsApi.toggle(key, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags-stats'] });
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: typeof newFlag) => featureFlagsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags-stats'] });
      setShowCreateModal(false);
      setNewFlag({ key: '', name: '', description: '', enabled: false, category: 'general' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (key: string) => featureFlagsApi.delete(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags-stats'] });
    },
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('es-MX', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  const handleToggle = (flag: FeatureFlag) => {
    if (!isSuperAdmin) return;
    toggleMutation.mutate({ key: flag.key, enabled: !flag.enabled });
  };

  const handleDelete = (key: string) => {
    if (!isSuperAdmin) return;
    if (confirm('¿Estas seguro de eliminar este feature flag?')) {
      deleteMutation.mutate(key);
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(newFlag);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feature Flags</h1>
          <p className="text-gray-600">Controla las funcionalidades del sistema</p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            + Nuevo Flag
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Total Flags</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Habilitados</p>
            <p className="text-2xl font-bold text-green-600">{stats.enabled}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Deshabilitados</p>
            <p className="text-2xl font-bold text-red-600">{stats.disabled}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Ratio Habilitados</p>
            <p className="text-2xl font-bold text-blue-600">
              {stats.total > 0 ? Math.round((stats.enabled / stats.total) * 100) : 0}%
            </p>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="flex space-x-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
            selectedCategory === 'all'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Todos
        </button>
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setSelectedCategory(key)}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === key
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Flags List */}
      <div className="bg-white rounded-lg shadow divide-y">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : flags && flags.length > 0 ? (
          flags.map((flag) => (
            <div key={flag.key} className="p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-900">{flag.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[flag.category]}`}>
                      {CATEGORY_LABELS[flag.category]}
                    </span>
                    <code className="text-xs text-gray-400 bg-gray-100 px-1 rounded">{flag.key}</code>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{flag.description}</p>
                  <div className="text-xs text-gray-400 mt-2">
                    Actualizado: {formatDate(flag.updatedAt)}
                    {flag.updatedBy && <span> por {flag.updatedBy}</span>}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {/* Toggle Switch */}
                  <button
                    onClick={() => handleToggle(flag)}
                    disabled={!isSuperAdmin || toggleMutation.isPending}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      flag.enabled ? 'bg-green-500' : 'bg-gray-300'
                    } ${!isSuperAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        flag.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>

                  {/* Delete Button */}
                  {isSuperAdmin && (
                    <button
                      onClick={() => handleDelete(flag.key)}
                      disabled={deleteMutation.isPending}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-8 text-center text-gray-500">
            No hay feature flags en esta categoria
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Crear Feature Flag</h2>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
                <input
                  type="text"
                  value={newFlag.key}
                  onChange={(e) => setNewFlag({ ...newFlag, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="mi_feature_flag"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Solo minusculas, numeros y guiones bajos</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={newFlag.name}
                  onChange={(e) => setNewFlag({ ...newFlag, name: e.target.value })}
                  placeholder="Mi Feature"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
                <textarea
                  value={newFlag.description}
                  onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })}
                  placeholder="Descripcion del feature flag..."
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <select
                  value={newFlag.category}
                  onChange={(e) => setNewFlag({ ...newFlag, category: e.target.value as FlagCategory })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={newFlag.enabled}
                  onChange={(e) => setNewFlag({ ...newFlag, enabled: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="enabled" className="text-sm text-gray-700">Habilitado por defecto</label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn-primary"
                >
                  {createMutation.isPending ? 'Creando...' : 'Crear Flag'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <InfoIcon className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium mb-1">Sobre los Feature Flags</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>General:</strong> Funcionalidades generales del sistema</li>
              <li><strong>Admin:</strong> Funcionalidades del panel de administracion</li>
              <li><strong>Usuario:</strong> Funcionalidades visibles para usuarios finales</li>
              <li><strong>Experimental:</strong> Features en desarrollo (usar con precaucion)</li>
            </ul>
            <p className="mt-2">
              Los cambios en feature flags se registran en el audit log.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
